import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  HostBinding,
  inject,
  input,
  model,
  OnChanges,
  signal,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import {
  IPagination,
  IQualificationCity,
  IQualificationMarker,
  IQualificationSummary,
  IUser,
  IUserQualificationCity,
  IUserQualificationMarker,
  IValuation,
  IValuationCity,
  IValuationMarker,
} from '@core/models';
import { Authentication, City, Geomarker } from '@core/services';
import { TranslateModule } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { RatingForm, ValorationsList } from '../modals';
import type { IRatingFormResult } from '../modals/rating-form/rating-form.component';
import { ValorationCard } from '../cards';
import { Rating } from '../rating/rating.component';
import { ValorationDetailItem } from './valoration-detail-item/valoration-detail-item.component';

@Component({
  selector: 'app-valorations-detail',
  imports: [
    TranslateModule,
    LazyLoadImageModule,
    ValorationCard,
    ValorationDetailItem,
    Rating,
  ],
  templateUrl: './valorations-detail.component.html',
  styleUrl: './valorations-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ValorationsDetail implements OnChanges {
  private readonly auth = inject(Authentication);
  private readonly cityService = inject(City);
  private readonly markerService = inject(Geomarker);
  private readonly matDialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  @HostBinding('class')
  protected get hostClass(): string {
    return this.variant() === 'geomarker' ? 'geomarker-valorations-host' : 'row px-3';
  }

  readonly variant = input<'default' | 'geomarker'>('default');
  readonly qualification = input(0);
  readonly qualificationSummary = input<IQualificationSummary[]>([]);
  readonly valorations = model<IValuation[]>([]);
  readonly city = model<number | null>(null);
  readonly marker = model<number | null>(null);
  readonly valuations = model<number>(0);

  protected readonly authenticated = signal(false);
  protected readonly user = signal<IUser | null>(this.auth.getAuthInformation());
  private userQualificationCity: IUserQualificationCity | null = null;
  private userQualificationMarker: IUserQualificationMarker | null = null;
  protected readonly userQualification = signal<number | null>(null);
  protected readonly hasQualification = signal(false);
  private nextPageValuation = '';
  protected readonly resultValuations = signal<IPagination<IValuation[]> | null>(null);
  protected readonly requestValuations = signal(false);
  protected readonly qualificationRequest = signal(false);
  protected readonly qualificationError = signal(false);
  private readonly userValoration = signal<{ id: number; detail: string } | null>(null);

  protected readonly previewValorations = computed(() => this.valorations().slice(0, 2));
  protected readonly totalValuations = computed(
    () => this.resultValuations()?.count ?? this.valuations(),
  );
  protected readonly totalRatings = computed(() =>
    this.qualificationSummary().reduce((sum, item) => sum + item.quantity, 0),
  );
  protected readonly ratingRows = computed(() => {
    const summaries = this.qualificationSummary();
    const total = summaries.reduce((sum, item) => sum + item.quantity, 0);
    return [5, 4, 3, 2, 1].map((score) => {
      const quantity = summaries.find((item) => Math.round(item.score) === score)?.quantity ?? 0;
      return { score, quantity, percentage: total ? Math.round((quantity / total) * 100) : 0 };
    });
  });

  constructor() {
    this.auth.onAuthenticate.pipe(takeUntilDestroyed()).subscribe((authenticated) => {
      this.authenticated.set(authenticated);
      this.hasQualification.set(false);
      this.userQualification.set(null);
      if (!authenticated) {
        this.user.set(null);
        this.userValoration.set(null);
      }
    });

    this.auth.onUpdateUser.pipe(takeUntilDestroyed()).subscribe((user) => {
      this.user.set(user);
      if (user && (this.city() || this.marker())) {
        this.getUserQualification();
        this.getValuations(undefined, true);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['city'] && !changes['marker']) return;

    const city = this.city();
    const marker = this.marker();
    if (city) {
      this.marker.set(null);
      this.userQualificationMarker = null;
    } else if (marker) {
      this.city.set(null);
      this.userQualificationCity = null;
    }

    this.userQualification.set(null);
    this.hasQualification.set(false);
    this.userValoration.set(null);
    this.valorations.set([]);
    this.resultValuations.set(null);
    this.nextPageValuation = '';

    if (city || marker) {
      if (this.user()) this.getUserQualification();
      this.getValuations();
    }
  }

  private async getUserQualification(): Promise<void> {
    const userId = Number(this.user()?.id);
    const city = this.city();
    const marker = this.marker();
    if (!userId || (!city && !marker)) return;

    try {
      if (city) {
        this.userQualificationCity = await lastValueFrom(
          this.cityService.qualificationByUser(city, userId),
        );
        this.userQualificationMarker = null;
        this.userQualification.set(Number(this.userQualificationCity.score));
      } else {
        this.userQualificationMarker = await lastValueFrom(
          this.markerService.qualificationByUser(marker!, userId),
        );
        this.userQualificationCity = null;
        this.userQualification.set(Number(this.userQualificationMarker.score));
      }
      this.hasQualification.set(true);
    } catch {
      this.userQualificationCity = null;
      this.userQualificationMarker = null;
      this.userQualification.set(null);
      this.hasQualification.set(false);
    }
  }

  private async getUserValoration(): Promise<void> {
    const userId = Number(this.user()?.id);
    const city = this.city();
    const marker = this.marker();
    if (!userId || (!city && !marker)) {
      this.userValoration.set(null);
      return;
    }

    try {
      const response = city
        ? await lastValueFrom(this.cityService.valuationsByUser(city, userId))
        : await lastValueFrom(this.markerService.valuationsByUser(marker!, userId));
      const records = response as unknown as Array<{ id: number; detail?: string }>;
      const current = Array.isArray(records) ? records[0] : undefined;
      this.userValoration.set(current ? { id: current.id, detail: current.detail ?? '' } : null);
    } catch {
      this.userValoration.set(null);
    }
  }

  private async saveQualification(qualification: number): Promise<void> {
    const user = Number(this.user()?.id);
    const city = this.city();
    const marker = this.marker();
    const score = `${qualification}`;

    if (city) {
      const data = { score, user, city };
      if (this.userQualificationCity) {
        await lastValueFrom(
          this.cityService.patchQualification(this.userQualificationCity.id, { score }),
        );
      } else {
        await lastValueFrom(this.cityService.addQualification(data as IQualificationCity));
      }
    } else if (marker) {
      const data = { score, user, marker };
      if (this.userQualificationMarker) {
        await lastValueFrom(
          this.markerService.editQualification(
            this.userQualificationMarker.id,
            data as IQualificationMarker,
          ),
        );
      } else {
        await lastValueFrom(this.markerService.addQualification(data as IQualificationMarker));
      }
    }
  }

  private async saveValoration(detail: string): Promise<void> {
    const user = Number(this.user()?.id);
    const city = this.city();
    const marker = this.marker();
    const current = this.userValoration();

    if (detail) {
      if (current) {
        if (city) await lastValueFrom(this.cityService.patchValuation(current.id, { detail }));
        if (marker) await lastValueFrom(this.markerService.patchValuation(current.id, { detail }));
        this.userValoration.set({ ...current, detail });
      } else if (city) {
        const response = await lastValueFrom(
          this.cityService.addValuation({ city, user, detail } as IValuationCity),
        );
        this.userValoration.set({ id: Number(response.id), detail });
      } else if (marker) {
        const response = await lastValueFrom(
          this.markerService.addValuation({ marker, user, detail } as IValuationMarker),
        );
        this.userValoration.set({ id: Number(response.id), detail });
      }
    } else if (current) {
      if (city) await lastValueFrom(this.cityService.deleteValuation(current.id));
      if (marker) await lastValueFrom(this.markerService.deleteValuation(current.id));
      this.userValoration.set(null);
    }
  }

  protected async getValuations(url?: string, reset = false): Promise<void> {
    if (this.requestValuations() && !reset) return;
    this.requestValuations.set(true);
    const firstPage = reset || !url;

    try {
      const response = this.city()
        ? await lastValueFrom(this.cityService.getValuations(this.city()!, url))
        : await lastValueFrom(this.markerService.getValuations(this.marker()!, url));
      this.updateValuations(response, firstPage);
    } catch (error) {
      console.error('Error fetching valuations:', error);
    } finally {
      this.requestValuations.set(false);
    }
  }

  private updateValuations(response: IPagination<IValuation[]>, firstPage: boolean): void {
    const current = firstPage ? [] : this.valorations();
    const merged = [...current, ...(response?.results ?? [])].filter(
      (item, index, all) => all.findIndex((candidate) => candidate.id === item.id) === index,
    );
    this.resultValuations.set(response);
    this.valorations.set(merged);
    this.valuations.set(response?.count ?? merged.length);
    this.nextPageValuation = response?.next ?? '';
  }

  protected openAuthForm(): void {
    this.auth.openLoginForm.next(true);
  }

  protected openValorationsModal(): void {
    const dialogRef = this.matDialog.open(ValorationsList, {
      width: 'min(760px, calc(100vw - 2rem))',
      maxWidth: 'calc(100vw - 2rem)',
      data: {
        city: this.city(),
        marker: this.marker(),
        valuations: this.valorations(),
        nextUrl: this.nextPageValuation,
        total: this.totalValuations(),
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.getValuations(undefined, true));
  }

  protected async editCalification(): Promise<void> {
    if (!this.authenticated()) {
      this.openAuthForm();
      return;
    }

    await this.getUserQualification();
    await this.getUserValoration();
    const dialogRef = this.matDialog.open(RatingForm, {
      width: 'min(500px, calc(100vw - 2rem))',
      maxWidth: 'calc(100vw - 2rem)',
      data: {
        qualification: this.userQualification() ?? 0,
        comment: this.userValoration()?.detail ?? '',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: IRatingFormResult | undefined) => {
        if (value) this.submitRating(value);
      });
  }

  private async submitRating(value: IRatingFormResult): Promise<void> {
    if (this.qualificationRequest()) return;

    this.qualificationRequest.set(true);
    this.qualificationError.set(false);
    this.userQualification.set(value.qualification);

    try {
      await this.saveQualification(value.qualification);
      await this.saveValoration(value.comment);
      await this.getUserQualification();
      await this.getValuations(undefined, true);
    } catch (error) {
      console.error('Error saving rating:', error);
      this.qualificationError.set(true);
    } finally {
      this.qualificationRequest.set(false);
    }
  }
}
